import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormField} from '@/components/shared/FormField'
import {MutedText} from '@/components/shared/MutedText'
import {StatusText} from '@/components/shared/StatusText'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Checkbox} from '@/components/ui/checkbox'
import {DatePicker} from '@/components/shared/DatePicker'
import type {WizardFormState} from '../hooks/useCertificationPolicyWizardForm'

export interface WizardStepBasicsProps {
    form: WizardFormState
    validToError: boolean
    onChange: <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => void
}

/**
 * Basics step — name, description, priority, active toggle, and the optional
 * validFrom/validTo validity window. Narrow prop contract (current form slice
 * in, change handler out) per spec §3.6 so issue 906 can extend it.
 */
export function WizardStepBasics({form, validToError, onChange}: WizardStepBasicsProps) {
    const i18n = useI18n()

    return (
        <Stack gap="lg" data-help-prefix="certificationPolicyWizard">
            <FormField label={String(i18n.certificationPolicies.wizard.nameLabel)} htmlFor="wizard-basics-name" required>
                <Input
                    id="wizard-basics-name"
                    value={form.name}
                    onChange={e => onChange('name', e.target.value)}
                    placeholder={String(i18n.certificationPolicies.wizard.namePlaceholder)}
                    data-help-key="basics.name"
                    data-testid="input-policy-name"
                />
            </FormField>

            <FormField label={String(i18n.certificationPolicies.description)} htmlFor="wizard-basics-description">
                <Textarea
                    id="wizard-basics-description"
                    value={form.description}
                    onChange={e => onChange('description', e.target.value)}
                    placeholder={String(i18n.certificationPolicies.wizard.descriptionPlaceholder)}
                    rows={3}
                    data-testid="input-policy-description"
                />
            </FormField>

            <FormField label={String(i18n.certificationPolicies.wizard.priorityLabel)} htmlFor="wizard-basics-priority" required>
                <Input
                    id="wizard-basics-priority"
                    type="number"
                    value={form.priority}
                    onChange={e => onChange('priority', parseInt(e.target.value, 10) || 0)}
                    placeholder={String(i18n.certificationPolicies.priorityPlaceholder)}
                    data-help-key="basics.priority"
                    data-testid="input-policy-priority"
                />
                <MutedText size="xs">{i18n.certificationPolicies.priorityHelp}</MutedText>
            </FormField>

            <InlineRow gap="md">
                <Checkbox
                    id="wizard-basics-active"
                    checked={form.active}
                    onCheckedChange={checked => onChange('active', checked === true)}
                    data-testid="checkbox-policy-active"
                />
                <Label htmlFor="wizard-basics-active">{i18n.certificationPolicies.wizard.activeLabel}</Label>
            </InlineRow>

            <FormField label={String(i18n.certificationPolicies.wizard.validFromLabel)} htmlFor="wizard-basics-valid-from">
                <DatePicker
                    testId="datepicker-valid-from"
                    value={form.validFrom}
                    onChange={iso => onChange('validFrom', iso)}
                    ariaLabel={String(i18n.certificationPolicies.wizard.validFromLabel)}
                />
                <MutedText size="xs" data-help-key="basics.validFrom">{i18n.certificationPolicies.wizard.validFromHelp}</MutedText>
            </FormField>

            <FormField label={String(i18n.certificationPolicies.wizard.validToLabel)} htmlFor="wizard-basics-valid-to">
                <DatePicker
                    testId="datepicker-valid-to"
                    value={form.validTo}
                    onChange={iso => onChange('validTo', iso)}
                    ariaLabel={String(i18n.certificationPolicies.wizard.validToLabel)}
                />
                <MutedText size="xs" data-help-key="basics.validTo">{i18n.certificationPolicies.wizard.validToHelp}</MutedText>
                {validToError && (
                    <StatusText tone="destructive" size="sm" data-testid="error-valid-to">
                        {i18n.certificationPolicies.wizard.validToAfterValidFrom}
                    </StatusText>
                )}
            </FormField>
        </Stack>
    )
}
